import React, { useCallback, useEffect, useState } from "react";
import { X, Loader2, Rocket, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { onboardingPlansApi, publicFormsApi } from "../lib/api";
import { FieldControl, FieldDisplayBlock, DISPLAY_ONLY_TYPES, fieldVisible } from "./marketing/formFields";
import EmbeddedStripeCheckoutModal from "./payments/EmbeddedStripeCheckoutModal";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * "Done for You Onboarding" purchase flow — plan picker -> Stripe checkout ->
 * intake form (form designated per-plan in Platform Console) -> confirmation.
 * All 4 steps stay inside this one modal.
 */
export default function OnboardingPlansModal({ open, onClose }) {
  const [step, setStep] = useState("plans"); // plans | intake | done
  const [plans, setPlans] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const [checkout, setCheckout] = useState(null); // { clientSecret, sessionId, plan }
  const [sessionId, setSessionId] = useState(null);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("plans");
    setCheckout(null);
    setSessionId(null);
    setForm(null);
    setValues({});
    onboardingPlansApi.list().then(setPlans).catch(() => setPlans([]));
  }, [open]);

  const onComplete = useCallback(async () => {
    const sid = checkout?.sessionId;
    if (!sid) { setCheckout(null); setBuyingId(null); return; }
    for (let i = 0; i < 6; i += 1) {
      const s = await onboardingPlansApi.status(sid).catch(() => null);
      if (s?.payment_status === "paid") {
        setCheckout(null);
        setBuyingId(null);
        setSessionId(sid);
        if (s.formId) {
          try {
            const f = await publicFormsApi.get(s.formId);
            setForm(f);
            setStep("intake");
          } catch {
            setStep("done");
          }
        } else {
          setStep("done");
        }
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.message("Payment still processing. Refresh in a moment.");
    setCheckout(null);
    setBuyingId(null);
  }, [checkout]);

  if (!open) return null;

  const buy = async (plan) => {
    setBuyingId(plan.id);
    try {
      const res = await onboardingPlansApi.checkout(plan.id, window.location.origin);
      if (res?.client_secret) {
        setCheckout({ clientSecret: res.client_secret, sessionId: res.session_id, plan });
      } else {
        toast.error("Could not start checkout.");
        setBuyingId(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setBuyingId(null);
    }
  };

  const onChange = (id, val) => setValues((v) => ({ ...v, [id]: val }));

  const submitIntake = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      await onboardingPlansApi.submitIntake(sessionId, values);
      setStep("done");
    } catch (err) {
      setFormError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    step === "intake" ? "Almost done — tell us about your business" : step === "done" ? "You're all set!" : "Done for You Onboarding";

  return (
    <>
      <div
        data-testid="onboarding-plans-modal"
        className="fixed inset-0 z-[210] flex items-center justify-center bg-[#070B16]/80 backdrop-blur-md p-4"
        onClick={step === "plans" ? onClose : undefined}
      >
        <div
          className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-2xl border border-slate-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative px-7 pt-6 pb-5 bg-gradient-to-br from-blue-600 to-indigo-600">
            {step === "plans" && (
              <button
                onClick={onClose}
                data-testid="onboarding-plans-close"
                className="absolute top-4 right-4 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                <Rocket className="h-6 w-6 text-white" />
              </div>
              <div>
                <div className="text-white font-bold text-[18px] leading-tight" data-testid="onboarding-plans-title">{title}</div>
                <div className="text-blue-100 text-[12.5px]">
                  {step === "plans" && "Let our team set everything up for you"}
                  {step === "intake" && "A few details so we can tailor your setup"}
                  {step === "done" && "Our team will be in touch shortly."}
                </div>
              </div>
            </div>
          </div>

          <div className="px-7 py-6">
            {step === "plans" && (
              plans === null ? (
                <div className="py-12 flex items-center justify-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading plans…
                </div>
              ) : plans.length === 0 ? (
                <div className="py-12 text-center text-[13.5px] text-slate-400" data-testid="onboarding-plans-empty">
                  No onboarding plans are available right now.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3.5">
                  {plans.map((plan) => (
                    <div
                      key={plan.id}
                      data-testid={`onboarding-plan-card-${plan.id}`}
                      className="rounded-2xl border-2 border-slate-200 hover:border-blue-300 p-4 flex flex-col transition-colors"
                    >
                      <div className="text-[15px] font-bold text-slate-900">{plan.name}</div>
                      {plan.description && (
                        <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{plan.description}</p>
                      )}
                      <div className="mt-2 text-[24px] font-extrabold text-slate-900">{money(plan.price)}</div>
                      <button
                        onClick={() => buy(plan)}
                        disabled={!!buyingId}
                        data-testid={`onboarding-plan-get-started-${plan.id}`}
                        className="mt-3 h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                      >
                        {buyingId === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        {buyingId === plan.id ? "Redirecting…" : "Get Started"}
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}

            {step === "intake" && form && (
              <form onSubmit={submitIntake} className="space-y-4" data-testid="onboarding-intake-form">
                {form.fields.filter((fld) => fieldVisible(fld, values)).map((fld) =>
                  DISPLAY_ONLY_TYPES.has(fld.type) ? (
                    <div key={fld.id}><FieldDisplayBlock field={fld} accent="#2563EB" /></div>
                  ) : (
                    <div key={fld.id}>
                      {fld.type !== "checkbox" && (
                        <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">
                          {fld.label}{fld.required && <span className="text-red-500 ml-0.5">*</span>}
                        </label>
                      )}
                      <FieldControl field={fld} value={values[fld.id]} onChange={(v) => onChange(fld.id, v)} accent="#2563EB" />
                    </div>
                  )
                )}
                {formError && <div className="text-[13px] text-red-600" data-testid="onboarding-intake-error">{formError}</div>}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="onboarding-intake-submit"
                  className="w-full h-11 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[14px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {form.submitLabel || "Submit"}
                </button>
              </form>
            )}

            {step === "done" && (
              <div className="py-8 flex flex-col items-center text-center" data-testid="onboarding-done">
                <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                </div>
                <p className="text-[15px] font-semibold text-slate-800 mb-1">You're all set!</p>
                <p className="text-[13.5px] text-slate-500 max-w-sm">Our team will be in touch shortly.</p>
                <button
                  onClick={onClose}
                  data-testid="onboarding-done-close"
                  className="mt-5 h-10 px-6 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[13.5px] font-semibold"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {checkout && (
        <EmbeddedStripeCheckoutModal
          clientSecret={checkout.clientSecret}
          title={checkout.plan?.name || "Done for You Onboarding"}
          subtitle={money(checkout.plan?.price)}
          onComplete={onComplete}
          onClose={() => { setCheckout(null); setBuyingId(null); }}
          testid="onboarding-embedded-checkout"
        />
      )}
    </>
  );
}
